/* ─── Header fijo ─────────────────────────────────────────────
   Fondo --blue-700 siempre, paridad exacta con AppHeader 'blue'
   del mobile. Sin variantes de scroll: una sola identidad. */

.navbar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: var(--z-sticky);
  height: var(--navbar-height);
  background: var(--blue-900);
  color: var(--color-text-inverse);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: var(--shadow-md);
}

.inner {
  position: relative;
  height: 100%;
  max-width: var(--container-max);
  margin-inline: auto;
  padding-inline: var(--space-5);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .inner {
    padding-inline: var(--space-8);
  }
}

/* ─── Logo ─────────────────────────────────────────────
   "Profind" en Bricolage Grotesque 800 sobre azul oscuro,
   con un punto jade que mantiene la identidad de marca. */

.brand {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  color: inherit;
  text-decoration: none;
  padding: var(--space-2) 0;
  border-radius: var(--radius-sm);
  transition: opacity var(--duration-fast) var(--ease-standard);
}

.brand:hover {
  opacity: 0.78;
}

.brandText {
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: var(--text-xl);
  letter-spacing: -0.02em;
  line-height: 1;
}

.brandDot {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  transform: translateY(-1px);
}

@media (min-width: 768px) {
  .brandText {
    font-size: var(--text-2xl);
  }
}

/* ─── Navegación central (desktop only) ─────────────────────
   Centrada en absoluto respecto al .inner para que quede
   geométricamente al medio sin importar el ancho del logo. */

.nav {
  display: none;
}

@media (min-width: 960px) {
  .nav {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.link {
  position: relative;
  font-family: var(--font-body);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--blue-100);
  text-decoration: none;
  padding: var(--space-2) 0;
  letter-spacing: 0.01em;
  transition: color var(--duration-fast) var(--ease-standard);
}

.link:hover {
  color: var(--color-text-inverse);
}

/* Subrayado animado: crece L→R en hover, fijo cuando está activa.
   Usa jade-500 para acentuar sobre el azul oscuro. */
.link::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 2px;
  background: var(--color-accent);
  border-radius: var(--radius-full);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform var(--duration-base) var(--ease-standard);
}

.link:hover::after {
  transform: scaleX(1);
}

.linkActive {
  color: var(--color-text-inverse);
  font-weight: 600;
}

.linkActive::after {
  transform: scaleX(1);
}

.linkLabel {
  position: relative;
}

/* ─── Acciones (CTA + hamburguesa) ───────────────────────── */

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.ctaDesktop {
  display: none;
}

@media (min-width: 768px) {
  .ctaDesktop {
    display: inline-flex;
  }
}

/* CTA mobile: pill azul brand con ícono de descarga. */
.ctaMobile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transition:
    background-color var(--duration-base) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

.ctaMobile:hover {
  background: var(--color-primary-hover);
  box-shadow: var(--shadow-md);
}

.ctaMobile:active {
  transform: scale(0.96);
}

.ctaMobileIcon {
  display: block;
}

@media (min-width: 768px) {
  .ctaMobile {
    display: none;
  }
}

/* ─── Hamburguesa ───────────────────────────────────────────
   3 líneas blancas que mutan a X al abrir. */

.hamburger {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  padding: 0;
  transition: background-color var(--duration-fast) var(--ease-standard);
}

.hamburger:hover {
  background: rgba(255, 255, 255, 0.10);
}

.hamburgerLine {
  display: block;
  width: 22px;
  height: 2px;
  border-radius: 2px;
  background: var(--color-text-inverse);
  transform-origin: center;
  transition:
    transform var(--duration-base) var(--ease-standard),
    opacity var(--duration-fast) var(--ease-standard);
}

.hamburgerOpen .hamburgerLine:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.hamburgerOpen .hamburgerLine:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.hamburgerOpen .hamburgerLine:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

@media (min-width: 960px) {
  .hamburger {
    display: none;
  }
}

/* ─── Menú móvil overlay ────────────────────────────────────
   Full-screen, slide desde la derecha. Continúa el azul oscuro
   del navbar para mantener identidad continua, sin cortes. */

.mobileMenu {
  position: fixed;
  inset: var(--navbar-height) 0 0 0;
  background: var(--blue-900);
  color: var(--color-text-inverse);
  z-index: calc(var(--z-sticky) - 1);
  transform: translateX(100%);
  visibility: hidden;
  transition:
    transform var(--duration-slow) var(--ease-standard),
    visibility 0s linear var(--duration-slow);
  overflow-y: auto;
}

.mobileMenuOpen {
  transform: translateX(0);
  visibility: visible;
  transition:
    transform var(--duration-slow) var(--ease-standard),
    visibility 0s linear 0s;
}

.mobileNav {
  display: flex;
  flex-direction: column;
  padding: var(--space-6) var(--space-5) var(--space-12);
  gap: var(--space-1);
}

/* Cada link entra escalonado (stagger por --i). */
.mobileLink {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5) var(--space-3);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-2xl);
  letter-spacing: -0.01em;
  color: var(--color-text-inverse);
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.10);
  opacity: 0;
  transform: translateY(8px);
  transition:
    color var(--duration-fast) var(--ease-standard),
    background-color var(--duration-fast) var(--ease-standard);
}

.mobileLink:hover,
.mobileLink:active {
  color: var(--color-accent);
}

.mobileMenuOpen .mobileLink {
  animation: mobile-link-in var(--duration-base) var(--ease-decelerate) forwards;
  animation-delay: calc(80ms + var(--i, 0) * 60ms);
}

.mobileLinkArrow {
  font-family: var(--font-body);
  font-size: var(--text-lg);
  color: rgba(255, 255, 255, 0.45);
  transition: transform var(--duration-fast) var(--ease-standard),
              color var(--duration-fast) var(--ease-standard);
}

.mobileLink:hover .mobileLinkArrow {
  color: var(--color-accent);
  transform: translateX(4px);
}

.mobileCta {
  margin-top: var(--space-8);
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: translateY(8px);
}

.mobileCta > * {
  width: 100%;
}

.mobileMenuOpen .mobileCta {
  animation: mobile-link-in var(--duration-base) var(--ease-decelerate) forwards;
  animation-delay: calc(80ms + var(--i, 0) * 60ms);
}

@keyframes mobile-link-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (min-width: 960px) {
  .mobileMenu {
    display: none;
  }
}

/* Accesibilidad: respetar prefers-reduced-motion. */
@media (prefers-reduced-motion: reduce) {
  .mobileMenuOpen .mobileLink,
  .mobileMenuOpen .mobileCta {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
